import { getToken, onMessage } from 'firebase/messaging';
import type { Unsubscribe } from 'firebase/messaging';
import { firebaseConfig, getFirebaseMessaging } from '../firebase';
import api from './api';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
export const NOTIFICATION_SETTINGS_CHANGED = 'ridesaathi:notification-settings-changed';

export interface NotificationSettingsSnapshot {
  enabled: boolean;
  periods: { start: string; end: string; enabled: boolean }[];
  emailNotifications: boolean;
  pushNotifications: boolean;
  soundEnabled: boolean;
  rideAlerts: {
    newRide: boolean;
    rideUpdate: boolean;
    rideCompletion: boolean;
  };
}

const defaultNotificationSettings: NotificationSettingsSnapshot = {
  enabled: true,
  periods: [],
  emailNotifications: true,
  pushNotifications: true,
  soundEnabled: true,
  rideAlerts: {
    newRide: true,
    rideUpdate: true,
    rideCompletion: true,
  },
};

export function normalizeNotificationSettings(
  raw: Partial<NotificationSettingsSnapshot> = {}
): NotificationSettingsSnapshot {
  return {
    ...defaultNotificationSettings,
    ...raw,
    periods: Array.isArray(raw.periods) ? raw.periods : [],
    rideAlerts: {
      ...defaultNotificationSettings.rideAlerts,
      ...(raw.rideAlerts || {}),
    },
  };
}

export function emitNotificationSettingsChanged(settings: Partial<NotificationSettingsSnapshot>): void {
  window.dispatchEvent(
    new CustomEvent<NotificationSettingsSnapshot>(NOTIFICATION_SETTINGS_CHANGED, {
      detail: normalizeNotificationSettings(settings),
    })
  );
}

export function onNotificationSettingsChanged(
  handler: (settings: NotificationSettingsSnapshot) => void
): () => void {
  const listener = (event: Event) => {
    handler(normalizeNotificationSettings((event as CustomEvent<NotificationSettingsSnapshot>).detail));
  };
  window.addEventListener(NOTIFICATION_SETTINGS_CHANGED, listener);
  return () => window.removeEventListener(NOTIFICATION_SETTINGS_CHANGED, listener);
}

function missingFirebasePushConfig(): string[] {
  return [
    !VAPID_KEY && 'REACT_APP_FIREBASE_VAPID_KEY',
    !firebaseConfig.apiKey && 'REACT_APP_FIREBASE_API_KEY',
    !firebaseConfig.projectId && 'REACT_APP_FIREBASE_PROJECT_ID',
    !firebaseConfig.messagingSenderId && 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    !firebaseConfig.appId && 'REACT_APP_FIREBASE_APP_ID',
  ].filter(Boolean) as string[];
}

/** Returns true when the Firebase env vars needed for push are present. */
export function isPushConfigured(): boolean {
  return missingFirebasePushConfig().length === 0;
}

export function getPushConfigProblem(): string | null {
  const missing = missingFirebasePushConfig();
  if (missing.length) {
    return `Push notifications are missing Firebase config: ${missing.join(', ')}.`;
  }

  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return 'Push notifications require HTTPS or localhost.';
  }

  return null;
}

export type PushResult =
  | { ok: true; token: string }
  | { ok: false; reason: 'not_configured' | 'not_supported' | 'permission_denied' | 'token_error'; message: string };

/**
 * Build the URL for the Firebase Messaging service worker, passing the Firebase
 * config as query params so the SW can initialise without hardcoded values.
 */
function buildSwUrl(): string {
  const url = new URL('/firebase-messaging-sw.js', window.location.origin);
  if (firebaseConfig.apiKey) url.searchParams.set('apiKey', firebaseConfig.apiKey);
  if (firebaseConfig.authDomain) url.searchParams.set('authDomain', firebaseConfig.authDomain);
  if (firebaseConfig.projectId) url.searchParams.set('projectId', firebaseConfig.projectId);
  if (firebaseConfig.messagingSenderId)
    url.searchParams.set('messagingSenderId', firebaseConfig.messagingSenderId);
  if (firebaseConfig.appId) url.searchParams.set('appId', firebaseConfig.appId);
  return url.toString();
}

/**
 * Requests notification permission, registers the Firebase service worker,
 * obtains the FCM token, and saves it to the backend.
 *
 * Returns a typed PushResult so callers can show actionable error messages.
 * Safe to call on every login — the legacy `requestAndRegisterToken` wrapper
 * below keeps backward-compat callers working.
 */
export async function requestPush(): Promise<PushResult> {
  const configProblem = getPushConfigProblem();
  if (configProblem) {
    return { ok: false, reason: 'not_configured', message: configProblem };
  }
  if (!('serviceWorker' in navigator) || !('Notification' in window)) {
    return { ok: false, reason: 'not_supported', message: 'Push notifications are not supported in this browser.' };
  }
  if (Notification.permission === 'denied') {
    return { ok: false, reason: 'permission_denied', message: 'Permission denied. Open browser site settings and allow notifications for this site, then try again.' };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { ok: false, reason: 'permission_denied', message: 'Permission denied. Open browser site settings and allow notifications for this site, then try again.' };
    }

    const swReg = await navigator.serviceWorker.register(buildSwUrl(), { scope: '/' });
    const readyReg = await navigator.serviceWorker.ready;
    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      return { ok: false, reason: 'not_configured', message: 'Firebase Messaging could not be initialised.' };
    }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: readyReg || swReg,
    });
    if (!token) {
      return { ok: false, reason: 'token_error', message: 'Could not obtain push token. Try refreshing the page.' };
    }

    await api.put('/api/users/profile', { fcmToken: token });
    return { ok: true, token };
  } catch (err: any) {
    console.warn('[FCM] Token registration failed:', err);
    const msg: string = err?.message ?? '';
    // DOMException from the browser's SubtleCrypto API — VAPID key is wrong length / bad format.
    if (msg.includes('ECDSA') || msg.includes('public key') || msg.includes('SubtleCrypto')) {
      return {
        ok: false,
        reason: 'token_error',
        message: 'Push setup failed: the VAPID key is invalid. Go to Firebase Console → Project Settings → Cloud Messaging → Web Push certificates and copy the full key (~87 characters).',
      };
    }
    return { ok: false, reason: 'token_error', message: msg || 'Push registration failed.' };
  }
}

/** @deprecated Use requestPush() for typed error handling. */
export async function requestAndRegisterToken(): Promise<string | null> {
  const result = await requestPush();
  return result.ok ? result.token : null;
}

export interface FcmPayload {
  notification?: { title?: string; body?: string };
  data?: Record<string, string>;
}

/**
 * Subscribe to foreground push messages (when the app tab is open).
 * Background messages are handled by the service worker.
 * Returns an unsubscribe function.
 */
export async function onForegroundMessage(
  handler: (payload: FcmPayload) => void
): Promise<Unsubscribe | (() => void)> {
  const messaging = await getFirebaseMessaging();
  if (!messaging) return () => {};
  return onMessage(messaging, handler);
}
